import { PercentUnitKind, PercentUnitOptions } from '../schema';
import { FormatValueOptions, UnitGroupConfig, UnitConfig } from './types';
import { hasDecimalPlaces, limitDecimalPlaces } from './utils';

export const PERCENT_GROUP_CONFIG: UnitGroupConfig = {
  label: 'Percent',
  decimal_places: true,
};
const PERCENT_GROUP = 'Percent';
export const PERCENT_UNIT_CONFIG: Readonly<Record<PercentUnitKind, UnitConfig>> = {
  Percent: {
    group: PERCENT_GROUP,
    label: 'Percent (0-100)',
  },
  PercentDecimal: {
    group: PERCENT_GROUP,
    label: 'Percent (0.0-1.0)',
  },
  '%': {
    // This option is not shown in the selector because it is a shorthand
    // duplicate of `Percent`.
    disableSelectorOption: true,
    group: PERCENT_GROUP,
    label: '%',
  },
};

type FormatPercentOptions = PercentUnitOptions & Pick<FormatValueOptions, 'trimZeros'>;

export function formatPercent(value: number, { kind, decimal_places, trimZeros = true }: FormatPercentOptions): string {
  const formatterOptions: Intl.NumberFormatOptions = {
    style: 'percent',
    useGrouping: true,
  };

  // We have kind: 'Percent' and kind: 'PercentDecimal'.
  // If 'Percent', 99 should become 99%
  // If 'PercentDecimal', 0.99 should become 99%
  // Intl.NumberFormat expects 0 -> 0%, 0.5 -> 50%, 1 -> 100%, etc.
  // So, if 'Percent', we need to divide by 100.
  if (kind === 'Percent') {
    value = value / 100;
  }

  if (hasDecimalPlaces(decimal_places)) {
    formatterOptions.maximumFractionDigits = limitDecimalPlaces(decimal_places);
    if (!trimZeros) {
      formatterOptions.minimumFractionDigits = limitDecimalPlaces(decimal_places);
    }
  } else {
    if (Math.abs(value) < 0.01) {
      // For values < 1%, allow up to 3 significant digits
      formatterOptions.maximumSignificantDigits = 3;
    } else {
      // For values >= 1%, allow up to 2 decimal places.
      formatterOptions.maximumFractionDigits = 2;
    }
  }

  const formatter = Intl.NumberFormat('en-US', formatterOptions);
  return formatter.format(value);
}
